/**
 * "Is this thing on?"
 *
 * The lobby's most important job after showing your face. A level meter is the
 * only way to answer it before joining, and answering it before joining is the
 * whole reason a lobby exists.
 *
 * Runs entirely outside React: the analyser loop writes `transform` and
 * `opacity` straight onto DOM nodes through refs. Driving twelve bars at 60 Hz
 * through `useState` would re-render the lobby sixty times a second, which is
 * both wasteful and, in a tree that also holds a live video element, visibly
 * janky.
 */

import * as React from 'react'
import { cn } from '@/lib/utils'

const BARS = 14

export function MicMeter({
  stream,
  muted,
  className,
}: {
  stream: MediaStream | null
  muted: boolean
  className?: string
}) {
  const bars = React.useRef<Array<HTMLSpanElement | null>>([])
  const mutedRef = React.useRef(muted)
  mutedRef.current = muted

  React.useEffect(() => {
    const track = stream?.getAudioTracks()[0]
    if (!track) return

    // `AudioContext` is created per meter and closed on cleanup. Browsers cap
    // the number of live contexts per document at a small number, so leaking
    // one per device change would silently stop the meter working after a few
    // switches.
    const AudioContextCtor: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextCtor) return

    const context = new AudioContextCtor()
    const source = context.createMediaStreamSource(new MediaStream([track]))
    const analyser = context.createAnalyser()
    analyser.fftSize = 1024
    analyser.smoothingTimeConstant = 0.6
    source.connect(analyser)

    const samples = new Uint8Array(analyser.fftSize)
    let frame = 0
    let smoothed = 0

    const tick = () => {
      frame = requestAnimationFrame(tick)
      analyser.getByteTimeDomainData(samples)

      let sum = 0
      for (const sample of samples) {
        const centred = (sample - 128) / 128
        sum += centred * centred
      }
      const rms = Math.sqrt(sum / samples.length)

      // Speech sits around 0.02-0.2 RMS, so a linear meter barely moves. The
      // curve below is a cheap stand-in for a dB scale that keeps normal
      // talking in the middle of the meter, where it is legible.
      const level = mutedRef.current ? 0 : Math.min(1, Math.pow(rms * 4.2, 0.62))

      // Fast attack, slow release: the ear expects a meter to jump to a
      // transient and fall back gently. Equal rates look like a flicker.
      smoothed = level > smoothed ? level : smoothed * 0.86 + level * 0.14

      const lit = smoothed * BARS
      for (let index = 0; index < BARS; index += 1) {
        const node = bars.current[index]
        if (!node) continue
        const fill = Math.max(0, Math.min(1, lit - index))
        node.style.opacity = String(0.18 + fill * 0.82)
        node.style.transform = `scaleY(${0.34 + fill * 0.66})`
      }
    }

    frame = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frame)
      source.disconnect()
      void context.close()
    }
  }, [stream])

  return (
    <div
      className={cn('flex h-5 items-center gap-[3px]', className)}
      role="meter"
      aria-label="Microphone level"
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {Array.from({ length: BARS }, (_, index) => (
        <span
          key={index}
          ref={(node) => {
            bars.current[index] = node
          }}
          className={cn(
            'h-full w-[3px] origin-center rounded-full transition-colors duration-200',
            muted ? 'bg-white/25' : 'bg-[#46cf9d]',
          )}
          style={{ opacity: 0.18, transform: 'scaleY(0.34)' }}
        />
      ))}
    </div>
  )
}
